import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../services/api";
import {
  extractArray,
  extractObject,
  normalizeNumericFields,
} from "../utils/apiData";
import {
  formatCurrency,
  formatDateForApi,
  formatInteger,
} from "../utils/formatters";

const COLORS = ["#c8102e", "#202020", "#6c757d", "#dc3545", "#8b0000", "#adb5bd"];

const SalesAnalytics = ({ filters }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [sellerData, setSellerData] = useState([]);
  const [advancedData, setAdvancedData] = useState({});
  const [searchInvoice, setSearchInvoice] = useState("");
  const [invoiceData, setInvoiceData] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [productPerformance, setProductPerformance] = useState([]);
  const [productLoading, setProductLoading] = useState(false);

  const buildParams = useCallback(
    () => ({
      start_date: formatDateForApi(filters.startDate),
      end_date: formatDateForApi(filters.endDate),
      shop_id: filters.shopId || undefined,
    }),
    [filters.endDate, filters.shopId, filters.startDate],
  );

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = buildParams();
      const [sellerRes, advancedRes] = await Promise.all([
        api.getSellerPerformance(params),
        api.getAdvancedAnalytics(params),
      ]);

      setSellerData(
        normalizeNumericFields(extractArray(sellerRes), [
          "total_transactions",
          "total_revenue",
          "total_items_sold",
          "avg_transaction_value",
          "total_discounts_given",
        ]),
      );

      const advanced = extractObject(advancedRes);
      setAdvancedData({
        ...advanced,
        discount_distribution: normalizeNumericFields(
          Array.isArray(advanced.discount_distribution)
            ? advanced.discount_distribution
            : [],
          ["count", "revenue_affected"],
        ),
        hourly_pattern: normalizeNumericFields(
          Array.isArray(advanced.hourly_pattern) ? advanced.hourly_pattern : [],
          ["hour", "revenue", "transactions"],
        ),
        weekly_pattern: normalizeNumericFields(
          Array.isArray(advanced.weekly_pattern) ? advanced.weekly_pattern : [],
          ["day_of_week", "revenue", "transactions"],
        ),
      });
    } catch (requestError) {
      setError(requestError.message || "Failed to fetch analytics data.");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleInvoiceSearch = async () => {
    const invoiceId = searchInvoice.trim();
    setActionError("");

    if (!invoiceId) {
      setInvoiceData([]);
      return;
    }

    setInvoiceLoading(true);
    try {
      const response = await api.getInvoiceDetails(invoiceId);
      setInvoiceData(
        normalizeNumericFields(extractArray(response), [
          "quantity",
          "price",
          "total",
          "discount",
        ]),
      );
    } catch (requestError) {
      setInvoiceData([]);
      setActionError(requestError.message || "Failed to fetch invoice details.");
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleProductSearch = async () => {
    const barcode = selectedProduct.trim();
    setActionError("");

    if (!barcode) {
      setProductPerformance([]);
      return;
    }

    setProductLoading(true);
    try {
      const response = await api.getProductPerformance({
        ...buildParams(),
        barcode,
      });
      setProductPerformance(
        normalizeNumericFields(extractArray(response), [
          "quantity_sold",
          "revenue",
          "avg_price",
          "transaction_count",
        ]),
      );
    } catch (requestError) {
      setProductPerformance([]);
      setActionError(
        requestError.message || "Failed to fetch product performance.",
      );
    } finally {
      setProductLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="content-loader">
        <Spinner animation="border" variant="danger" />
        <span>Loading advanced analytics…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Analytics could not load</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchAnalytics}>
          Retry
        </Button>
      </Alert>
    );
  }

  const discountDistribution = Array.isArray(advancedData.discount_distribution)
    ? advancedData.discount_distribution
    : [];
  const hourlyPattern = Array.isArray(advancedData.hourly_pattern)
    ? advancedData.hourly_pattern
    : [];

  return (
    <div>
      <div className="section-heading">
        <div>
          <p className="eyebrow">Detailed insights</p>
          <h1>Advanced Analytics</h1>
        </div>
        <Button variant="outline-dark" onClick={fetchAnalytics}>
          Refresh Data
        </Button>
      </div>

      {actionError && (
        <Alert variant="warning" dismissible onClose={() => setActionError("")}>
          {actionError}
        </Alert>
      )}

      <Row className="mb-4 g-3">
        <Col lg={8}>
          <Card className="h-100">
            <Card.Header>Seller Performance</Card.Header>
            <Card.Body>
              {sellerData.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={sellerData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="sold_by" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip
                      formatter={(value, name) =>
                        name === "Revenue" ? formatCurrency(value) : value
                      }
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="total_revenue"
                      fill="#c8102e"
                      name="Revenue"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="total_transactions"
                      stroke="#202020"
                      strokeWidth={3}
                      name="Transactions"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-chart">No seller data for this period.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="h-100">
            <Card.Header>Discount Distribution</Card.Header>
            <Card.Body>
              {discountDistribution.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={discountDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => entry.discount_range}
                      outerRadius={90}
                      dataKey="count"
                    >
                      {discountDistribution.map((entry, index) => (
                        <Cell
                          key={`${entry.discount_range || "range"}-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-chart">No discount data for this period.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col lg={12}>
          <Card>
            <Card.Header>Hourly Sales Pattern</Card.Header>
            <Card.Body>
              {hourlyPattern.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={hourlyPattern}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip
                      formatter={(value, name) =>
                        name === "Revenue" ? formatCurrency(value) : value
                      }
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="revenue"
                      fill="#c8102e"
                      name="Revenue"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="transactions"
                      stroke="#202020"
                      name="Transactions"
                      strokeWidth={3}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-chart">No hourly sales data for this period.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header>Invoice Details</Card.Header>
            <Card.Body>
              <div className="d-flex mb-3">
                <Form.Control
                  type="text"
                  placeholder="Enter Invoice ID"
                  value={searchInvoice}
                  onChange={(event) => setSearchInvoice(event.target.value)}
                  className="me-2"
                  onKeyDown={(event) =>
                    event.key === "Enter" && handleInvoiceSearch()
                  }
                />
                <Button
                  variant="danger"
                  onClick={handleInvoiceSearch}
                  disabled={invoiceLoading}
                >
                  {invoiceLoading ? <Spinner size="sm" animation="border" /> : "Search"}
                </Button>
              </div>

              {invoiceData.length ? (
                <div className="table-responsive">
                  <Table striped hover size="sm">
                    <thead>
                      <tr>
                        <th>Barcode</th>
                        <th>Name</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData.map((item, index) => (
                        <tr key={`${item.barcode || "item"}-${index}`}>
                          <td>{item.barcode}</td>
                          <td>{item.name}</td>
                          <td>{formatInteger(item.quantity)}</td>
                          <td>{formatCurrency(item.price)}</td>
                          <td>{formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="empty-search-result">
                  Enter an invoice ID to view line items.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="h-100">
            <Card.Header>Product Performance</Card.Header>
            <Card.Body>
              <div className="d-flex mb-3">
                <Form.Control
                  type="text"
                  placeholder="Enter Product Barcode"
                  value={selectedProduct}
                  onChange={(event) => setSelectedProduct(event.target.value)}
                  className="me-2"
                  onKeyDown={(event) =>
                    event.key === "Enter" && handleProductSearch()
                  }
                />
                <Button
                  variant="danger"
                  onClick={handleProductSearch}
                  disabled={productLoading}
                >
                  {productLoading ? <Spinner size="sm" animation="border" /> : "Search"}
                </Button>
              </div>

              {productPerformance.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={productPerformance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip
                      formatter={(value, name) =>
                        name === "Revenue" ? formatCurrency(value) : value
                      }
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="revenue"
                      fill="#c8102e"
                      name="Revenue"
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="quantity_sold"
                      fill="#343a40"
                      name="Quantity Sold"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-search-result">
                  Enter a barcode to view product performance.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={12}>
          <Card>
            <Card.Header>Seller Performance Details</Card.Header>
            <Card.Body className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Seller</th>
                    <th>Transactions</th>
                    <th>Items Sold</th>
                    <th>Revenue</th>
                    <th>Avg Transaction</th>
                    <th>Discounts Given</th>
                  </tr>
                </thead>
                <tbody>
                  {sellerData.length ? (
                    sellerData.map((seller, index) => (
                      <tr key={`${seller.sold_by || "seller"}-${index}`}>
                        <td><strong>{seller.sold_by || "Unknown"}</strong></td>
                        <td>{formatInteger(seller.total_transactions)}</td>
                        <td>{formatInteger(seller.total_items_sold)}</td>
                        <td>{formatCurrency(seller.total_revenue)}</td>
                        <td>{formatCurrency(seller.avg_transaction_value)}</td>
                        <td>{formatCurrency(seller.total_discounts_given)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No seller data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SalesAnalytics;
